import ColorFilter from '../../../Component/ColorFilter';
import Interactable from '../../../Component/Interactable';
import { IGameState } from '../../../State/Game/IGameState';
import { ProductID } from '../../../State/Player/Product';
import { hasPurchaseOverflow } from '../../../State/User/PurchaseHelper';
import { UserActions } from '../../../State/User/UserActions';
import BaseUISystem from '../BaseUISystem';
import {
    applyDefaultButtonAnimation,
    removeBounceButtonAnimation,
} from '../UIHelper';
import BaseMenu from './BaseMenu';
import { UIActions } from '../../../State/UI/UIActions';
import Game from '../../../Game';
import UITextComponent from '../../../Component/UI/Text';
import { AUDIO_UI_BUTTON_CLOSE, AUDIO_UI_BUTTON_DEFAULT } from '../../Audio';
import GameObject from '../../../GameObject/GameObject';
import { GameObjectEvent } from '../../../GameObject/GameObjectEvent';
import { StartAnimation } from '../../Animation/AnimationHelper';
import ElementComponent from '../../../Component/UI/Element';
import Renderable from '../../../Component/Renderable';
import { IAPCatalog } from '../../../State/Game/IAP';

const OFFER: string = 'main_container/offer';
const SPECIAL_OFFER: string = 'main_container/special_offer';
const SPECIAL_JOKER: string = 'main_container/special_joker';
const BTN_CLOSE: string = 'main_container/close_button';

interface IProductData {
    gameObject: GameObject;
    id: ProductID;
}

export default class ShopMenuHandler extends BaseMenu {
    private catalog: IAPCatalog;
    private products: IProductData[];

    constructor(uiSystem: BaseUISystem) {
        super('shopMenu', uiSystem);

        this.resources.set(
            'atlas_shop',
            'resource/graphics/export/atlas_ui_shop.json'
        );
    }

    public handleStateChanged(state: IGameState): void {
        this.products.forEach(product =>
            this.handleProductState(state, product)
        );
        if (
            state.platform.IAPIsReady &&
            !state.previousState.platform.IAPIsReady
        ) {
            this.catalog = Game.sdk.getCatalog();
            // Set the prices.
            this.products.forEach(product => this.setPrice(product));
        }
    }

    protected onCreate() {
        this.products = [
            /* prettier-ignore */ { gameObject: this.element.find(OFFER + '1'), id: ProductID.ID_007_COINS_2k },
            /* prettier-ignore */ { gameObject: this.element.find(OFFER + '2'), id: ProductID.ID_008_COINS_6k },
            /* prettier-ignore */ { gameObject: this.element.find(OFFER + '3'), id: ProductID.ID_009_COINS_22k },
            /* prettier-ignore */ { gameObject: this.element.find(OFFER + '4'), id: ProductID.ID_010_COINS_100k },
            /* prettier-ignore */ { gameObject: this.element.find(SPECIAL_OFFER), id: ProductID.ID_011_SPECIAL_OFFER },
            /* prettier-ignore */ { gameObject: this.element.find(SPECIAL_JOKER), id: ProductID.ID_012_SPECIAL_JOKER },
        ];

        // Set translations.
        this.setText(
            this.products[4].gameObject.find('badge/text'),
            Game.locale.getText('SpecialOffer').replace(' ', '\n')
        );
        this.setText(
            this.products[4].gameObject.find('item2/text'),
            `3x ${Game.locale.getText('Jokers')}`
        );
        this.setText(
            this.products[4].gameObject.find('item4/text'),
            `7x ${Game.locale.getText('Lives')}`
        );
        this.setText(
            this.products[5].gameObject.find('item1/text'),
            `30,000 ${Game.locale.getText('Coins')}`
        );
        this.setText(
            this.products[5].gameObject.find('item2/text'),
            `15x ${Game.locale.getText('Jokers')}`
        );

        this.setText(
            this.products[5].gameObject.find('item1/text'),
            `30,000 ${Game.locale.getText('Coins')}`
        );

        if (Game.state.getState().platform.IAPIsReady) {
            this.catalog = Game.sdk.getCatalog();
        }
        const btnClose = this.element.find(BTN_CLOSE);
        btnClose.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(UIActions.hidePopup(this.name));
        });
        applyDefaultButtonAnimation(btnClose, AUDIO_UI_BUTTON_CLOSE);

        this.products.forEach(product => {
            this.handleProductState(Game.state.getState(), product);
        });
    }

    protected onShow(params?: any) {
        this.showProducts();
    }

    protected onShown(params?: any): void {
        this.animateProducts();
    }

    private showProducts() {
        // Set the prices.
        this.products.forEach(product => this.setPrice(product));
        this.products
            .map(p => p.gameObject)
            .forEach(p => {
                p.getComponent(ElementComponent).scale.x = -1;
                p.children.forEach(child => {
                    child.getComponent(Renderable).visible = false;
                });
            });
    }

    private animateProducts() {
        StartAnimation(this.element, 'ShopMenu', {
            products: this.products.map(p => p.gameObject),
            specialOffer: this.products[4].gameObject,
            specialJoker: this.products[5].gameObject,
        });
    }

    private handleProductState(state: IGameState, product: IProductData): void {
        const btn = product.gameObject.find('button');
        if (!btn || btn.destroyed) return;

        if (hasPurchaseOverflow(state, product.id)) {
            this.setButtonInactive(btn);
        } else {
            this.setButtonActive(btn, product.id);
        }
    }

    private setText(gameObject: GameObject, text: string): void {
        gameObject.getComponent(UITextComponent).text = text;
    }
    private setPrice(product: IProductData): void {
        const btn = product.gameObject.find('button');
        if (!this.catalog) {
            this.setText(
                product.gameObject.find('button/price'),
                Game.locale.getText('Loading')
            );
            this.setButtonInactive(btn);
        } else {
            this.setText(
                product.gameObject.find('button/price'),
                this.getProductPrice(product)
            );
            if (hasPurchaseOverflow(Game.state.getState(), product.id)) {
                this.setButtonInactive(btn);
            } else {
                this.setButtonActive(btn, product.id);
            }
        }
    }

    private getProductPrice(product: IProductData): string {
        const productData = this.catalog.products.find(
            p => p.productId === product.id
        );
        if (productData) return productData.price;

        const btn = product.gameObject.find('button');

        this.setButtonInactive(btn);

        return Game.locale.getText('Loading');
    }

    private setButtonInactive(btn: GameObject) {
        btn.addComponent(ColorFilter);
        btn.getComponent(ColorFilter).saturation = 0;
        removeBounceButtonAnimation(btn);
        btn.off(GameObjectEvent.SELECTED);
    }

    private setButtonActive(btn: GameObject, id: ProductID) {
        btn.removeComponent(ColorFilter);
        btn.getComponent(Interactable).enabled = true;
        applyDefaultButtonAnimation(btn, AUDIO_UI_BUTTON_DEFAULT);
        btn.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(UserActions.purchaseItem(id));
        });
    }
}
